// Shared pixel UI pieces: buttons, frames, keyword text, numbers, sprite icons.
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { PALETTE } from '../art/palette';
import { SPRITES_BY_ID } from '../art/sprites';
import { audio } from '../audio/audio';
import { formatNumber } from '../i18n/format';
import { spans, t } from '../i18n/t';
import type { Decimal } from '../sim/decimal';
import { useGame } from '../state/store';

export function Btn(props: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  kind?: 'normal' | 'primary' | 'danger' | 'ghost' | 'gold';
  title?: string;
  style?: CSSProperties;
  className?: string;
  testId?: string;
}) {
  const { kind = 'normal' } = props;
  return (
    <button
      className={`pbtn pbtn-${kind} ${props.className ?? ''}`}
      disabled={props.disabled}
      title={props.title}
      style={props.style}
      data-testid={props.testId}
      onMouseEnter={() => !props.disabled && audio.play('hover', { volume: 0.3 })}
      onClick={() => {
        if (props.disabled) return;
        audio.play('click');
        props.onClick?.();
      }}
    >
      {props.children}
    </button>
  );
}

export function Frame(props: { children: ReactNode; className?: string; style?: CSSProperties; title?: string }) {
  return (
    <div className={`pframe ${props.className ?? ''}`} style={props.style}>
      {props.title && <div className="pframe-title">{props.title}</div>}
      {props.children}
    </div>
  );
}

/** Renders a translated string with [value]…[/] keyword markup as coloured spans. */
export function KText({ text }: { text: string }) {
  return (
    <>
      {spans(text).map((s, i) =>
        s.kind ? (
          <span key={i} className={`kw kw-${s.kind}`}>
            {s.text}
          </span>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

export function T({ k, p }: { k: string; p?: Record<string, string | number> }) {
  return <KText text={t(k, p)} />;
}

export function Num({ v }: { v: Decimal | number | string }) {
  const notation = useGame((g) => g.meta.settings.notation);
  return <>{formatNumber(v, notation)}</>;
}

export function useFmt(): (v: Decimal | number | string) => string {
  const notation = useGame((g) => g.meta.settings.notation);
  return (v) => formatNumber(v, notation);
}

const urlCache = new Map<string, string>();
export function spriteUrl(id: string, frame = 0): string {
  const key = `${id}#${frame}`;
  const hit = urlCache.get(key);
  if (hit) return hit;
  const def = SPRITES_BY_ID.get(id);
  if (!def) return '';
  const c = document.createElement('canvas');
  c.width = def.w;
  c.height = def.h;
  const ctx = c.getContext('2d')!;
  (def.frames[frame] ?? def.frames[0]!).forEach((row, y) =>
    [...row].forEach((ch, x) => {
      const k = def.colors[ch];
      if (!k || ch === '.' || ch === ' ') return;
      ctx.fillStyle = '#' + PALETTE[k].toString(16).padStart(6, '0');
      ctx.fillRect(x, y, 1, 1);
    }),
  );
  const url = c.toDataURL();
  urlCache.set(key, url);
  return url;
}

export function Icon({ id, scale = 1, style, title }: { id: string; scale?: number; style?: CSSProperties; title?: string }) {
  const def = SPRITES_BY_ID.get(id);
  const url = useMemo(() => spriteUrl(id), [id]);
  if (!def) return null;
  return (
    <img
      src={url}
      alt=""
      title={title}
      draggable={false}
      className="picon"
      style={{ width: def.w * scale, height: def.h * scale, ...style }}
    />
  );
}

export function Bar({ value, max, color = 'gold' }: { value: number; max: number; color?: keyof typeof PALETTE }) {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const cells = 20;
  const lit = Math.round(pct * cells);
  return (
    <div className="pbar">
      {Array.from({ length: cells }, (_, i) => (
        <span key={i} style={{ background: i < lit ? '#' + PALETTE[color].toString(16).padStart(6, '0') : undefined }} />
      ))}
    </div>
  );
}
